import { UserFriendlyError } from "@ckweb/common/utils";

type FallbackOptions<T_Data> = {
  fallback?: ((result: NonNullable<T_Data> | undefined) => unknown) | NonNullable<T_Data>;
  /** if mapError returns undefined, the error will not be processed */
  mapError?: ((error: unknown) => string | Error) | string | Error;
};

export type FallbackReturnType<O extends FallbackOptions<T_Data> | undefined, T_Data> = O extends {
  fallback: (args: T_Data) => unknown;
}
  ? ReturnType<O["fallback"]>
  : T_Data;

type BaseSafeExecutionOptions = {
  enableGlobalNotifications?: boolean;
  showGlobalLoadingIndicator?: boolean;
  executionName?: string;
};

export type SafeExecutionOptions<T_Data> = BaseSafeExecutionOptions & FallbackOptions<T_Data>;

function hasFallbackValue<T>(options?: unknown): options is FallbackOptions<T> {
  if (!options || typeof options !== "object") return false;

  return "fallback" in options;
}

function hasErrorMapper<T>(options?: unknown): options is FallbackOptions<T> {
  if (!options || typeof options !== "object") return false;

  return "mapError" in options;
}

function handleFallback<T_Data>(
  result: T_Data | undefined,
  options: FallbackOptions<T_Data> | undefined,
): FallbackReturnType<typeof options, T_Data> {
  return (
    hasFallbackValue(options)
      ? options.fallback instanceof Function
        ? options.fallback(result ?? undefined)
        : (result ?? options.fallback)
      : result
  ) as FallbackReturnType<typeof options, T_Data>;
}

function handleError<O>(error: unknown, options: O) {
  return hasErrorMapper(options)
    ? options.mapError instanceof Function
      ? options.mapError(error)
      : (error ?? options.mapError)
    : error;
}

export function useSafeExecution() {
  // Can't use useI18n here because this composable will be used in middlewares
  const {
    $i18n: { t, te },
    addNotificationMessage,
    runProcess,
  } = useFetchContext();

  /**
   * Handles the safe execution of a promise with loading indicator, error handling and notifications.
   * @param realWork The promise to execute.
   * @param options Options for handling the execution.
   * @returns The result of the promise or undefined if an error occurred.
   */
  async function handleSafeExecution<T_Data>(
    realWork: PromiseOrFunction<T_Data | undefined>,
    options?: SafeExecutionOptions<T_Data>,
  ): Promise<FallbackReturnType<typeof options, T_Data>> {
    const executionName = options && "executionName" in options ? options.executionName : undefined;
    try {
      const work = $ck.utils.unwrapPromise(realWork);

      const showGlobalLoadingIndicator = options?.showGlobalLoadingIndicator;
      const promise = showGlobalLoadingIndicator ? runProcess(work, { processName: executionName }) : work;
      const result: T_Data | undefined = await promise;

      return handleFallback(result, options);
    } catch (e) {
      const error = handleError(e, options);
      const userFriendlyMessage =
        error instanceof UserFriendlyError ? error.message : typeof error === "string" ? error : undefined;

      const enableGlobalNotifications = options?.enableGlobalNotifications;

      if (enableGlobalNotifications && userFriendlyMessage) {
        devConsole.error("Friendly error caught in app", error, userFriendlyMessage);
        const message = te(userFriendlyMessage) ? t(userFriendlyMessage) : userFriendlyMessage;
        addNotificationMessage(message);
      } else {
        devConsole.error("Error caught in app", error, e);
      }
    }

    return handleFallback(undefined as T_Data | undefined, options);
  }

  function wrapSafeExecution<T_Data, Args extends unknown[] = []>(
    realWork: (...args: Args) => Promise<T_Data>,
    options?: SafeExecutionOptions<T_Data>,
  ) {
    return (...params: Args) => handleSafeExecution(realWork(...params), options);
  }

  return {
    handleSafeExecution,
    wrapSafeExecution,
  };
}
